import Decimal from 'decimal.js';
import { DateTime, Interval } from 'luxon';
import { ActivityTagName, BillableType, Entity, TimesheetDuration, TimesheetEntry, TimesheetLog, TimesheetResource, TimesheetTable, WorkRole, WorkType } from '../entities';
import { DateUtility } from './date.utility';

const HourInSecond = 3600;
// The Unit of all duration here is using second
export class TimesheetUtility {


    //Methods to call when fields change
    //Call this after setting the resource on the timesheet
    public static resourceChanged(timesheet: TimesheetEntry, resource: TimesheetResource): TimesheetEntry {

        timesheet.rate.cost = resource?.hourlyCostRate ?? 0;
        TimesheetUtility.calculateCost(timesheet);

        timesheet.workRole = resource.workRole;
        TimesheetUtility.workRoleChanged(timesheet, resource.workRole);

        timesheet.timezone = timesheet.timezone ?? resource.timezone;

        return timesheet;
    }

    public static workRoleChanged(timesheet: TimesheetEntry, workRole: WorkRole): TimesheetEntry {
        //TODO: Set the sell rate
        //Need to have the smarts to find related pricebook if required
        //This one is a bit of a turd, but logic should all be self contained in this class
        //Need to identify if this is a timesheet for a job/wo or quote or whatever,
        //to determine if there is a sell pricebook that we should be checking
        timesheet.rate.sell = workRole?.sellRate;
        return TimesheetUtility.calculateAmount(timesheet);
    }

    public static sellRateChanged(timesheet: TimesheetEntry, sell: number): TimesheetEntry {
        timesheet.rate.sell = sell;
        return TimesheetUtility.calculateAmount(timesheet);
    }

    public static workTypeChanged(timesheet: TimesheetEntry, workType: WorkType): TimesheetEntry {
        //TODO set billing options for the timesheet

        timesheet.billable = workType?.billable ?? BillableType.Billable;

        timesheet.billingOptions = {
            increment: workType?.increment ?? 0,
            multiplier: workType?.multiplier ?? 1,
            minimumDuration: workType?.minimumDuration ?? 0
        };

        return TimesheetUtility.calculateBillableDuration(timesheet);
    }

    public static durationChanged(timesheet: TimesheetEntry, duration: number): TimesheetEntry {
        timesheet.duration.duration = duration;
        if (timesheet?.start?.time) {
            const end = TimesheetUtility.getEndTime(new Date(timesheet?.start?.time), duration);
            timesheet.end.time = end;
        }
        return TimesheetUtility.calculateTimesheet(timesheet);
    }

    public static deductibleChanged(timesheet: TimesheetEntry): TimesheetEntry {
        return TimesheetUtility.calculateTimesheet(timesheet);
    }

    public static billableDurationChanged(timesheet: TimesheetEntry): TimesheetEntry {
        return TimesheetUtility.calculateAmount(timesheet);
    }

    public static billableStatusChanged(timesheet: TimesheetEntry): TimesheetEntry {
        return TimesheetUtility.calculateAmount(timesheet);
    }

    public static calculateTimesheet(timesheet: TimesheetEntry, changeCustomBillable = true): TimesheetEntry {

        //Calculate the cost, this method does not trigger any further changes
        TimesheetUtility.calculateCost(timesheet);
        //Set off the process by just calculating billableDuration
        TimesheetUtility.calculateBillableDuration(timesheet, changeCustomBillable);

        return timesheet;
    }

    // duration is miliseconds so we need to convert to hour
    public static calculateCost(timesheet: TimesheetEntry): TimesheetEntry {

        let duration = TimesheetUtility.getBillingDuration(timesheet);
        duration = TimesheetUtility.toHour(duration);
        const costRate = timesheet?.rate?.cost ?? 0;

        const cost = new Decimal(duration * costRate).toDP(2).toNumber();

        timesheet.total.cost = cost ?? 0;
        return timesheet;
    }

    public static calculateAmount(timesheet: TimesheetEntry): TimesheetEntry {

        if (timesheet.billable == BillableType.Billable) {

            const billableDuration = TimesheetUtility.toHour(timesheet?.duration?.customBillable);

            const sellRate = timesheet?.rate?.sell ?? 0;

            const amount = new Decimal(billableDuration * sellRate).toDP(2).toNumber();

            timesheet.total.amount = amount ?? 0;
        } else
            timesheet.total.amount = 0;

        return timesheet;
    }

    public static calculateBillableDuration(timesheet: TimesheetEntry, changeCustomBillable = true): TimesheetEntry {

        const calculatedBillable = TimesheetUtility.getBillingDuration(timesheet);

        //Set defaults if they are not available
        const increment = timesheet?.billingOptions?.increment ?? 0;
        const multiplier = timesheet?.billingOptions?.multiplier ?? 1;
        const minimum = timesheet?.billingOptions?.minimumDuration ?? 0;

        let incrementedDuration = calculatedBillable;

        if (increment !== 0) {
            const ceilDuration = Math.ceil(calculatedBillable / increment);
            incrementedDuration = ceilDuration * increment;
        }

        const billableDuration = new Decimal(Math.max(incrementedDuration * multiplier, minimum)).toDP(2).toNumber();

        timesheet.duration.billable = billableDuration;
        timesheet.duration.customBillable = changeCustomBillable
            ? billableDuration
            : timesheet.duration.customBillable;

        return TimesheetUtility.calculateAmount(timesheet);
    }

    public static getEndTime(start: Date, duration: number): Date {
        const end = DateUtility.getDateTime(start).plus({ 'second': duration }).toString();

        return new Date(end);
    }

    public static toTimesheetTable(timesheet: TimesheetEntry): TimesheetTable {

        return {
            _id: timesheet?._id,
            date: timesheet?.date,
            workType: timesheet?.workType?.name,
            workRole: timesheet?.workRole?.name,
            resource: timesheet?.resource,
            duration: TimesheetUtility.getBillingDuration(timesheet),
            billable: timesheet?.duration?.customBillable,
            workId: timesheet?.entity?.name,
            invoiced: !!timesheet?.invoice,
            status: timesheet?.status,
            links: timesheet?.links,
            notes: timesheet?.notes
        } as TimesheetTable;
    }


    public static fixStartAndEndTime(timesheet: TimesheetEntry): TimesheetEntry {

        const startOfOnDate = DateUtility.getDateTime(timesheet?.date).startOf('day');
        const startTime = DateUtility.getDateTime(timesheet?.start?.time);
        const startToMinute = startTime?.hour * 60 + startTime?.minute;

        timesheet.start.time = startOfOnDate.plus({ 'minute': startToMinute }).toString() as any;
        timesheet.end.time = DateUtility.getDateTime(timesheet.start?.time).plus({ 'second': timesheet?.duration?.duration }).toString() as any;

        return timesheet;
    }

    public static toHour(value: number): number {
        return value / HourInSecond;
    }

    public static getBillingDuration(timesheet: TimesheetEntry): number {
        const duration = Math.max(timesheet?.duration?.duration - timesheet?.duration?.deductible, 0);
        return duration;
    }

    public static autoFixStartAndEndTime(timesheet: TimesheetEntry, end: Date): TimesheetEntry {
        const startOfOnDate = DateUtility.getDateTime(timesheet?.date).startOf('day'); // 11.05
        const startTime = DateUtility.getDateTime(timesheet?.start?.time);
        // Actual start time is utc. E.g If I'm in Melbourne and start a timesheet at 10 am (GMT +10)
        // then actual start time is 0:00 in utc. Timesheet's start time is a bit different which is
        // 10 am utc (easy to display everywhere but need to review when we support multi timezone).
        const actualStartTime = DateUtility.getDateTime(timesheet?.actualStart?.time);
        const startToMinute = startTime?.hour * 60 + startTime?.minute;

        // End time is also in utc (like actualStartTime) so we can calculate the duration base on
        // these two.
        const endTime = DateUtility.getDateTime(end);
        const duration = endTime.diff(actualStartTime).as('second');

        timesheet.start.time = startOfOnDate.plus({ 'minute': startToMinute }).toString() as any;
        if (timesheet.end != null) {
            timesheet.end.time = startTime.plus({ 'second': duration }).toString() as any;
        }
        else {
            timesheet.end = {
                time: startTime.plus({ 'second': duration }).toString() as any,
            } as TimesheetLog
        }

        timesheet.actualStart.time = timesheet.start.time;
        timesheet.actualEnd = {
            time: timesheet.end.time,
            location: null,
            autoCompleted: true,
        }

        timesheet.duration = {
            duration: duration,
            deductible: 0,
            capture: duration,
        } as TimesheetDuration;

        return timesheet;
    }

    public static async fixBrokenLinks(timesheet: TimesheetEntry, getJob: (id: string) => Promise<Entity>): Promise<TimesheetEntry> {
        const workOrderLink = timesheet.links.find(x => x?.name === ActivityTagName.WorkOrderId);
        if (workOrderLink?.value) {
            const jobLink = timesheet.links.find(x => x?.name === ActivityTagName.JobId);
            const job = await getJob(workOrderLink.value);

            if (job) {
                if (!jobLink || jobLink.value !== job._id) {
                    timesheet.links = timesheet.links.filter(x => x?.name !== ActivityTagName.JobId);
                    timesheet.links.push({
                        name: ActivityTagName.JobId,
                        value: job._id,
                    });
                }
            }
        }

        return timesheet;
    }

    public static fixTimesheetDuration(timesheet: TimesheetEntry, start: DateTime | Date, end: DateTime | Date): TimesheetEntry {

        let startValue = DateUtility.getDateTime(start);
        const endValue = DateUtility.getDateTime(end);

        if (!startValue || !endValue) {
            return timesheet
        }

        const startToMinutes = startValue.hour * 60 + startValue.minute;
        const endToMinutes = endValue.hour * 60 + endValue.minute;

        let duration = 0;

        if (startToMinutes > endToMinutes) {

            const startTimeToEndOfDay = Interval.fromDateTimes(startValue, startValue.endOf('day')).length('second');
            const startOfDayToEndTime = Interval.fromDateTimes(endValue.startOf('day'), endValue).length('second');

            duration = startTimeToEndOfDay + startOfDayToEndTime;

        } else {
            // COMMENT: If the start and end date are different, we need to calculate the correct duration.
            // E.g start is 4:05 PM and end is 6:05 PM, the duration should be 2 hours and on the same day.
            // This case usually happen when user manually change the start and end time in the UI which drive
            // the calendar component to different date.
            if (startValue.day !== endValue.day) {
                const startOfTimesheetDate = DateUtility.getDateTime(timesheet.date).startOf('day');

                const startTimeToEndOfDay = DateUtility.calcStartTimeToEndOfDay(startValue);
                const correctStartDate = DateUtility.getDateTime(startOfTimesheetDate).plus({ seconds: startTimeToEndOfDay })

                const startOfDayToEndTime = DateUtility.calcStartOfDayToEndTime(endValue);
                const correctEndDate = DateUtility.getDateTime(startOfTimesheetDate).plus({ seconds: startOfDayToEndTime })

                duration = Interval.fromDateTimes(correctStartDate, correctEndDate).length('second');

                startValue = correctStartDate;
            }
            else {
                duration = Interval.fromDateTimes(startValue, endValue).length('second');
            }

        }

        const originalStartValue = DateUtility.getDateTime(timesheet?.start?.time);

        // COMMENT: Checking which field is actually changed because the call to durationChanged
        // will be based on start time and the new duration to calculate the end time so we need
        // to update the start time only if it is changed.
        if (startValue !== originalStartValue && timesheet?.start) {
            timesheet.start.time = startValue.toJSDate();
        }

        timesheet = TimesheetUtility.durationChanged(timesheet, duration);

        return timesheet;
    }

    public static updateLinks(timesheet: TimesheetEntry): TimesheetEntry {
        /**
         * If the timesheet has a resource, we need to add a link to the resource
         * This is to ensure that the timesheet is linked to the resource
         * and that the resource is linked to the timesheet
         */
        if (timesheet.resource && !timesheet.links?.find(x => x.value === timesheet.resource._id)) {
            if (!timesheet.links) {
                timesheet.links = [];
            }

            timesheet.links.push({
                name: ActivityTagName.ResourceId,
                value: timesheet.resource._id
            });
        }

        return timesheet;
    }
}